import type { Controller } from "../../../services/licensing/client/types.gen";
import {
  CONTROLLER_STATUS_COLUMNS,
  controllerSortKey,
  groupControllersByStatus,
} from "./controllerStatus";

export type ControllersView = "target" | "running" | "status";

export type BoardColumn = {
  key: string;
  title: string;
  hint: string;
  /** The version this column stands for, absent on the "no version" column. */
  version?: string;
  controllers: Controller[];
};

export const statusBoardColumns = (
  controllers: Controller[],
): BoardColumn[] => {
  const grouped = groupControllersByStatus(controllers);
  return CONTROLLER_STATUS_COLUMNS.map((column) => ({
    key: column.status,
    title: column.title,
    hint: column.hint,
    controllers: grouped[column.status],
  }));
};

type ParsedVersion = { release: number[]; prerelease: string[] };

const parseVersion = (version: string): ParsedVersion => {
  // Build metadata (after "+") carries no precedence per semver.
  const withoutBuild = version.split("+")[0];
  const hyphenAt = withoutBuild.indexOf("-");
  const core = hyphenAt === -1 ? withoutBuild : withoutBuild.slice(0, hyphenAt);
  return {
    release: core.split(".").map(Number),
    prerelease:
      hyphenAt === -1 ? [] : withoutBuild.slice(hyphenAt + 1).split("."),
  };
};

const isNumericIdentifier = (identifier: string) => /^\d+$/.test(identifier);

/** Semver precedence; the backend validates versions, so no error handling. */
export const compareVersions = (left: string, right: string): number => {
  const a = parseVersion(left);
  const b = parseVersion(right);

  for (let i = 0; i < Math.max(a.release.length, b.release.length); i++) {
    const diff = (a.release[i] ?? 0) - (b.release[i] ?? 0);
    if (diff !== 0) {
      return diff;
    }
  }

  if (a.prerelease.length === 0 || b.prerelease.length === 0) {
    // A release outranks any of its prereleases.
    return b.prerelease.length - a.prerelease.length;
  }
  for (let i = 0; i < Math.max(a.prerelease.length, b.prerelease.length); i++) {
    const l = a.prerelease[i];
    const r = b.prerelease[i];
    if (l === undefined || r === undefined) {
      return l === undefined ? -1 : 1;
    }
    if (isNumericIdentifier(l) && isNumericIdentifier(r)) {
      const diff = Number(l) - Number(r);
      if (diff !== 0) {
        return diff;
      }
    } else if (isNumericIdentifier(l) !== isNumericIdentifier(r)) {
      // Numeric identifiers rank below alphanumeric ones.
      return isNumericIdentifier(l) ? -1 : 1;
    } else if (l !== r) {
      return l < r ? -1 : 1;
    }
  }
  return 0;
};

type VersionColumnLabels = {
  noVersionTitle: string;
  noVersionHint: string;
  hintFor: (version: string) => string;
};

/**
 * One column per version, oldest to newest, with the controllers that have no
 * such version in a leading column.
 */
const versionBoardColumns = (
  controllers: Controller[],
  versionOf: (controller: Controller) => string | null | undefined,
  labels: VersionColumnLabels,
  alwaysShown: string[] = [],
): BoardColumn[] => {
  const byVersion = new Map<string, Controller[]>(
    alwaysShown.map((version) => [version, []]),
  );
  const withoutVersion: Controller[] = [];

  for (const controller of controllers) {
    const version = versionOf(controller);
    if (version) {
      const bucket = byVersion.get(version) ?? [];
      bucket.push(controller);
      byVersion.set(version, bucket);
    } else {
      withoutVersion.push(controller);
    }
  }

  const columns: BoardColumn[] = [
    {
      key: "none",
      title: labels.noVersionTitle,
      hint: labels.noVersionHint,
      controllers: withoutVersion,
    },
    ...[...byVersion.keys()].sort(compareVersions).map((version) => ({
      key: version,
      title: version,
      hint: labels.hintFor(version),
      version,
      controllers: byVersion.get(version)!,
    })),
  ];

  for (const column of columns) {
    column.controllers.sort((left, right) =>
      controllerSortKey(left).localeCompare(controllerSortKey(right)),
    );
  }
  return columns;
};

export const runningVersionBoardColumns = (
  controllers: Controller[],
): BoardColumn[] =>
  versionBoardColumns(controllers, (controller) => controller.current_version, {
    noVersionTitle: "Unknown",
    noVersionHint:
      "Never reported a running version. Versions only come in once a controller has reported over MQTT at least once.",
    hintFor: (version) =>
      `Last reported running ${version}, regardless of the version each controller is targeted at.`,
  });

/**
 * Every version in play gets a column, including ones only seen as a running
 * version or added by hand, so a controller can be dragged onto a version
 * nothing targets yet.
 */
export const targetVersionBoardColumns = (
  controllers: Controller[],
  addedVersions: string[] = [],
): BoardColumn[] =>
  versionBoardColumns(
    controllers,
    (controller) => controller.target_version,
    {
      noVersionTitle: "No target",
      noVersionHint:
        "No target version set, so nothing can be deployed. Drag a controller onto a version to set one.",
      hintFor: (version) =>
        `Targeted at ${version} on the next deploy, regardless of what each controller reports running now.`,
    },
    [
      ...controllers.flatMap((controller) =>
        controller.current_version ? [controller.current_version] : [],
      ),
      ...addedVersions,
    ],
  );
